import React from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Chip,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarToday,
  Visibility,
  Timer,
  ImageOutlined,
} from '@mui/icons-material';

const getFeaturedImageUrl = (post) => {
  const featuredMedia = post?.media?.find?.(
    (m) => m.collection_name === 'featured_image'
  );
  return (
    featuredMedia?.preview_url ||
    featuredMedia?.original_url ||
    post?.featured_image?.url ||
    post?.featured_image_url ||
    null
  );
};

const PostCard = ({ post, onClick, ...props }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lng = i18n.language;

  const handleClick = () => {
    if (onClick) {
      onClick(post);
    } else {
      navigate(`/blog/${post.slug}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(lng === 'ar' ? 'ar-KW' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const imageUrl = getFeaturedImageUrl(post);
  const dateValue = post.published_at || post.created_at;

  return (
    <Card
      sx={{
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        overflow: 'hidden',
        background: theme.palette.mode === 'dark'
          ? 'rgba(26, 26, 26, 0.95)'
          : theme.palette.background.paper,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 6px rgba(0, 0, 0, 0.3)'
          : '0 2px 8px rgba(0, 0, 0, 0.08)',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 12px 24px rgba(0, 0, 0, 0.4)'
            : `0 12px 24px ${theme.palette.primary.main}25`,
          borderColor: theme.palette.primary.main,
        },
        '&:hover .post-card-image': {
          transform: 'scale(1.05)',
        },
        ...props.sx
      }}
      onClick={handleClick}
    >
      {/* Featured Badge */}
      {post.is_featured && (
        <Chip
          label={t('blog.featured', 'Featured')}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.65rem',
            height: 24,
            zIndex: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        />
      )}

      {/* Post Image */}
      <Box sx={{ position: 'relative', overflow: 'hidden', aspectRatio: '16 / 9', backgroundColor: theme.palette.action.hover }}>
        {imageUrl ? (
          <CardMedia
            component="img"
            image={imageUrl}
            alt={post.title?.[lng] || post.title?.en || 'post image'}
            className="post-card-image"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.4s ease',
            }}
          />
        ) : (
          <Box
            className="post-card-image"
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}22, ${theme.palette.primary.light}11)`,
              transition: 'transform 0.4s ease',
            }}
          >
            <ImageOutlined sx={{ fontSize: 56, color: theme.palette.primary.main, opacity: 0.5 }} />
          </Box>
        )}
      </Box>

      {/* Post Content */}
      <CardContent sx={{ flexGrow: 1, pb: 2, pt: 2 }}>
        {/* Category */}
        {post.category && (
          <Chip
            label={post.category.name?.[lng] || post.category.name?.en}
            size="small"
            sx={{
              mb: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
              color: 'white',
              fontSize: '0.7rem',
              height: 26,
              fontWeight: 500,
            }}
          />
        )}

        {/* Title */}
        <Typography
          variant="h6"
          fontWeight="600"
          sx={{
            mb: 1.5,
            height: 64,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.3,
            color: theme.palette.text.primary,
            fontSize: '1.1rem',
          }}
        >
          {post.title?.[lng] || post.title?.en}
        </Typography>

        {/* Excerpt */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            height: 52,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.4,
          }}
        >
          {post.excerpt?.[lng] || post.excerpt?.en}
        </Typography>

        {/* Post Meta */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto' }}>
          {/* Date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
            <Typography variant="caption" color="text.secondary">
              {formatDate(dateValue)}
            </Typography>
          </Box>

          {/* Reading Time */}
          {post.reading_time && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Timer sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
              <Typography variant="caption" color="text.secondary">
                {post.reading_time} {t('blog.minRead', 'min read')}
              </Typography>
            </Box>
          )}

          {/* View Count */}
          {post.view_count && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Visibility sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
              <Typography variant="caption" color="text.secondary">
                {post.view_count}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
            {post.tags.slice(0, 3).map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name?.[lng] || tag.name?.en}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.65rem',
                  height: 22,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main + '10',
                  }
                }}
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PostCard;
